import { format, parseISO } from 'date-fns'
import { th } from 'date-fns/locale'
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Skeleton } from '@/common/ui/skeleton'
import useQueryGetAdminPartnerDailyTransactions from '@/modules/manage-player/api/hooks/useQueryGetAdminPartnerDailyTransactions'

interface AdminPartnerTransactionChartProps {
	partnerId: string
	startDate?: string
	endDate?: string
}

const formatNumber = (value: number) =>
	value.toLocaleString('th-TH', {
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	})

export default function AdminPartnerTransactionChart({
	partnerId,
	startDate,
	endDate,
}: AdminPartnerTransactionChartProps) {
	const payload = startDate && endDate ? { startDate, endDate } : { days: 7 }

	const { data, isLoading } = useQueryGetAdminPartnerDailyTransactions(
		partnerId,
		payload,
	)

	const chartData = (data?.data ?? []).map((item) => ({
		date: format(parseISO(item.date), 'd MMM', { locale: th }),
		depositAmount: item.depositAmount,
		depositBonusAmount: item.depositBonusAmount,
		totalWithdraw: item.totalWithdraw,
	}))

	const titleSuffix =
		startDate && endDate ? `${startDate} - ${endDate}` : '7 วันล่าสุด'

	return (
		<Card>
			<CardHeader className="pb-2">
				<CardTitle className="text-base font-medium">
					ยอดฝาก/ถอน ({titleSuffix})
				</CardTitle>
			</CardHeader>
			<CardContent className="pt-4">
				{isLoading ? (
					<div className="flex h-[250px] items-center justify-center">
						<Skeleton className="h-full w-full" />
					</div>
				) : chartData.length === 0 ? (
					<div className="flex h-[250px] items-center justify-center text-muted-foreground">
						ไม่มีข้อมูล
					</div>
				) : (
					<ResponsiveContainer width="100%" height={250}>
						<LineChart
							data={chartData}
							margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
						>
							<CartesianGrid
								strokeDasharray="3 3"
								stroke="hsl(var(--border))"
							/>
							<XAxis dataKey="date" tick={{ fontSize: 12 }} />
							<YAxis
								tick={{ fontSize: 12 }}
								tickFormatter={(value) => formatNumber(value)}
							/>
							<Tooltip
								formatter={(value: number, name: string) => [
									formatNumber(value),
									name === 'depositAmount'
										? 'ยอดฝาก'
										: name === 'depositBonusAmount'
											? 'โบนัส'
											: 'ยอดถอน',
								]}
								contentStyle={{
									backgroundColor: 'hsl(var(--card))',
									border: '1px solid hsl(var(--border))',
									borderRadius: '8px',
								}}
							/>
							<Legend
								formatter={(value: string) =>
									value === 'depositAmount'
										? 'ยอดฝาก'
										: value === 'depositBonusAmount'
											? 'โบนัส'
											: 'ยอดถอน'
								}
							/>
							<Line
								type="monotone"
								dataKey="depositAmount"
								stroke="#22c55e"
								strokeWidth={2}
								dot={{ fill: '#22c55e', r: 4 }}
								activeDot={{ r: 6 }}
							/>
							<Line
								type="monotone"
								dataKey="depositBonusAmount"
								stroke="#3b82f6"
								strokeWidth={2}
								dot={{ fill: '#3b82f6', r: 4 }}
								activeDot={{ r: 6 }}
							/>
							<Line
								type="monotone"
								dataKey="totalWithdraw"
								stroke="#ef4444"
								strokeWidth={2}
								dot={{ fill: '#ef4444', r: 4 }}
								activeDot={{ r: 6 }}
							/>
						</LineChart>
					</ResponsiveContainer>
				)}
			</CardContent>
		</Card>
	)
}
